import { useLazyQuery, useMutation } from '@apollo/client/react/hooks'
import { useTheme } from '@material-ui/core'
import { Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import AdotelSkeleton from '../../components/AdotelSkeleton'
import apolloClient from '../../graphql/apolloClient'
import {
  CHANNEL_GENERATE_MUTATION,
  CHANNEL_MODIFY_MUTATION,
  CHANNEL_QUERY
} from '../../graphql/channel'
import { lang } from '../../localization'
import { Channel } from '../../types/channel'
import { ChannelBasicDetails } from './ChannelBasicDetails'
import { ChannelFooter } from './ChannelFooter'
import useStyles from './styles/ChannelMainForm.styles'
import { channelFormValidationSchema } from './validations/channelFormValidation'

const CHANNEL_FORM_INIT_VALUES: Channel = {
  basicDetails: {
    name: '',
    name2: '',
    channelCode: '',
    code: '',
    displayChannelCode: '',
    IATA: '',
    channelType: undefined,
    currency: undefined,
    channelID: '',
    shortName: '',
    shortNameTranslatedList: [
      { label: 'Iran, Islamic Republic of', value: '', nativeLabel: 'فارسی' }
    ],
    showProviderName: false
  }
}

const ChannelMainForm = () => {
  const classes = useStyles()
  const history = useHistory()
  const params = useParams<{ channelCode: string }>()

  const theme = useTheme()

  const [channelOnSubmitFunction, { loading: loadingForChannel }] = useMutation(
    params.channelCode ? CHANNEL_MODIFY_MUTATION : CHANNEL_GENERATE_MUTATION,
    {
      notifyOnNetworkStatusChange: true
    }
  )

  const [getInitialValues, { loading }] = useLazyQuery<{
    channel: Channel
  }>(CHANNEL_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onError: (err) => {
      toast.error(err.message)
    }
  })

  const [initialValues, setinitialValues] = useState<Channel>()

  const getInitialValuesFunction = async () => {
    if (params.channelCode) {
      const { data, error } = await getInitialValues({
        variables: { channelCode: params.channelCode }
      })
      if (!error && data) {
        setinitialValues({
          ...data?.channel!,
          basicDetails: {
            ...data?.channel.basicDetails,
            code: data?.channel.basicDetails.displayChannelCode || ''
          }
        })
      } else if (error) {
        toast.error(error.message)
      }
    }
  }

  const handleSubmit = async (values: Channel) => {
    const { channelID, displayChannelCode, channelCode, ...newBasicDetails } =
      values.basicDetails
    const newValues = { ...values, basicDetails: newBasicDetails }

    try {
      const { data: response } = await channelOnSubmitFunction(
        params.channelCode
          ? {
              variables: {
                input: newValues,
                channelCode: params.channelCode
              }
            }
          : {
              variables: {
                input: newValues
              }
            }
      )
      if (params.channelCode) {
        toast.success(response.modifyChannel.message)
      } else {
        toast.success(response.generateChannel.message)
      }
      apolloClient.cache.evict({
        fieldName: 'channels'
      })
      history.push(`/${lang}/channels`)
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message)
      } else {
        toast.error('ERROR!')
      }
    }
  }

  useEffect(() => {
    getInitialValuesFunction()
  }, [])

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues || CHANNEL_FORM_INIT_VALUES}
      onSubmit={handleSubmit}
      validationSchema={channelFormValidationSchema}>
      {({
        values,
        setFieldValue,
        errors,
        touched,
        handleBlur,
        isSubmitting
      }) => {
        return (
          <>
            <Form>
              <ChannelBasicDetails
                {...{
                  values,
                  setFieldValue,
                  errors,
                  touched,
                  handleBlur,
                  isSubmitting,
                  classes
                }}
              />
              <ChannelFooter
                isSubmitting={isSubmitting}
                loadingForChannel={loadingForChannel}
              />
            </Form>
            <AdotelSkeleton
              open={loading}
              circlesColor={theme.palette.primary.main}
              numberOfCircles={4}
            />
          </>
        )
      }}
    </Formik>
  )
}

export default ChannelMainForm
